<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
    title: { type: String, default: '미리보기' },
    width: { type: String, default: '60rem' },
    height: { type: String, default: '50rem' },
    fileUrl: { type: String, required: true },
    fileType: { type: String, required: true },
    downurl: { type: String }
});

const emit = defineEmits(['close']);
const panel = ref(null);
const pdfContainer = ref(null);
const pageNum = ref(1);
const pageCount = ref(0);
let offsetX = 0;
let offsetY = 0;
let isDragging = false;
let pdfDoc = null;
const downloadFile = ref({ url: '' });
const isImage = computed(() => ['image/jpeg', 'image/jpg', 'image/png'].includes(props.fileType)); // , 'image/gif', 'image/bmp'
const isPdf = computed(() => props.fileType === 'application/pdf');
const isNotImageOrPdf = computed(() => !isImage.value && !isPdf.value);

const renderPage = async (num) => {
    const page = await pdfDoc.getPage(num);
    const viewport = page.getViewport({ scale: 1 }); // 항상 원본 크기로 렌더링

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const renderContext = {
        canvasContext: context,
        viewport: viewport
    };

    await page.render(renderContext).promise;

    pdfContainer.value.innerHTML = '';
    pdfContainer.value.appendChild(canvas);
};

const loadPDF = async () => {
    try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();

        pdfDoc = await pdfjsLib.getDocument(props.fileUrl).promise;
        pageCount.value = pdfDoc.numPages;

        if (pageCount.value === 0) {
            openPDFInNewTab();
            return;
        }

        await renderPage(pageNum.value);
    } catch (error) {
        console.error('Error loading PDF:', error);
        openPDFInNewTab();
    }
};

const openPDFInNewTab = () => {
    window.open(props.fileUrl, '_blank');
    emit('close');
};

const prevPage = () => {
    if (pageNum.value <= 1) return;
    pageNum.value--;
    renderPage(pageNum.value);
};

const nextPage = () => {
    if (pageNum.value >= pageCount.value) return;
    pageNum.value++;
    renderPage(pageNum.value);
};

const onMouseDown = (event) => {
    offsetX = event.clientX - panel.value.offsetLeft;
    offsetY = event.clientY - panel.value.offsetTop;
    isDragging = true;
};

const onMouseMove = (event) => {
    if (isDragging) {
        panel.value.style.left = `${event.clientX - offsetX}px`;
        panel.value.style.top = `${event.clientY - offsetY}px`;
    }
};

const onMouseUp = () => {
    isDragging = false;
};

onMounted(async () => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    if (isNotImageOrPdf.value) {
        // downloadFiles();
        // emit('close');
    } else if (props.fileType === 'application/pdf') {
        await loadPDF();
    }
});

onUnmounted(() => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
});

const downloadFiles = (event) => {
    window.open(props.downurl, '_self');
    setTimeout(() => {
        downloadFile.value = { url: '' };
    }, 300);
};
</script>

<template>
    <div class="preview-panel" :style="{ width: width, height: isPdf ? '65rem' : height }" ref="panel">
        <div class="preview-panel-header" @mousedown="onMouseDown">
            <h2>{{ title }}</h2>
            <div>
                <!-- <button @click.stop="downloadFiles" class="preview-panel-close-button mr-4">다운로드</button> -->

                <button class="down-button" @click.stop="downloadFiles">
                    <i class="pi pi-download"></i>
                </button>

                <button class="close-button" @click="$emit('close')">
                    <i class="pi pi-times"></i>
                </button>
            </div>
            <div v-if="downloadFile.url" class="file-preview-container">
                <object :data="downloadFile.url" height="0"><a :href="downloadFile.url" target="_self">여기</a>를 클릭하여 다운로드하세요.</object>
            </div>
        </div>
        <div class="preview-panel-content">
            <template v-if="isImage">
                <img :src="fileUrl" alt="Image preview" class="preview-image" />
            </template>
            <template v-else-if="isPdf">
                <div class="pdf-container" ref="pdfContainer"></div>
                <div class="pdf-controls">
                    <button @click="prevPage" :disabled="pageNum <= 1">이전</button>
                    <span>{{ pageNum }} / {{ pageCount }}</span>
                    <button @click="nextPage" :disabled="pageNum >= pageCount">다음</button>
                </div>
            </template>
            <template v-else>
                <p>이 파일 형식은 미리보기를 지원하지 않습니다.</p>
            </template>
        </div>
    </div>
</template>

<style scoped>
.preview-panel {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    opacity: 0.95;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    padding: 1.8rem;
    z-index: 1500;
    display: flex;
    flex-direction: column;
    max-height: 90vh; /* 뷰포트 높이의 90%로 제한 */
    overflow: hidden; /* 전체 패널의 스크롤 방지 */
}

.preview-panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    cursor: move;
    border-bottom: 0.1rem solid #ddd;
    padding-bottom: 0.8rem;
    font-size: 1.3rem;
    font-weight: bold;
}

.preview-panel-close-button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: normal;
    color: black;
}

.preview-panel-close-button:hover {
    font-weight: bold;
    color: #0038ea;
}

.preview-panel-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: auto; /* 내용이 넘칠 경우 스크롤 허용 */
}

.preview-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.pdf-container {
    flex: 1;
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
}

/* 스크롤바 스타일 (webkit 브라우저용) */
.preview-panel-content::-webkit-scrollbar,
.pdf-container::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.preview-panel-content::-webkit-scrollbar-track,
.pdf-container::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.preview-panel-content::-webkit-scrollbar-thumb,
.pdf-container::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.preview-panel-content::-webkit-scrollbar-thumb:hover,
.pdf-container::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Firefox용 스크롤바 스타일 */
.preview-panel-content,
.pdf-container {
    scrollbar-width: thin;
    scrollbar-color: #888 #f1f1f1;
}

.pdf-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem;
    gap: 1rem;
}

.pdf-controls button {
    padding: 0.5rem 1rem;
    background-color: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
}

.pdf-controls button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.close-button {
    color: #b7b9bd;
    cursor: pointer;
    margin-left: 1rem;
    background: none; /* Remove background */
    border: none; /* Remove border */
    padding: 0;
    line-height: 1; /* Align text vertically */
}

.close-button:hover {
    color: #000; /* Slightly darker on hover */
}

.down-button {
    color: #0038ea;
    cursor: pointer;
    margin-left: 1rem;
    background: none; /* Remove background */
    border: none; /* Remove border */
    padding: 0;
    line-height: 1; /* Align text vertically */
}

.down-button:hover {
    color: #5680fa;
}
</style>
